import { type Writable, writable } from 'svelte/store';
import type { ModelConfig } from '$lib/apis';
import type { Banner } from '$lib/types';
import type { Socket } from 'socket.io-client';
import type { UserCoordinates } from '$lib/utils';
import type { Chat } from './chat';
export { keycloak, initKeycloak, getKeycloak, cleanupKeycloak } from './keycloak';


// Backend
export const WEBUI_NAME = writable('NEBUL PrivateGPT');

export const config = writable<Config | null>(null);
export const user: Writable<SessionUser | undefined> = writable(undefined);

// Helper function to refresh backend config
export const refreshBackendConfig = async () => {
	try {
		const { getBackendConfig } = await import('$lib/apis');
		const backendConfig = await getBackendConfig();
		config.set(backendConfig);
		WEBUI_NAME.set(backendConfig.name);
		return backendConfig;
	} catch (error) {
		console.error('Error refreshing backend config:', error);
		throw error;
	}
};

// Frontend
export const MODEL_DOWNLOAD_POOL = writable({});

export const mobile = writable(false);

export const socket: Writable<null | Socket> = writable(null);
export const activeUserCount: Writable<null | number> = writable(null);
export const USAGE_POOL: Writable<null | string[]> = writable(null);

export const theme = writable('system');

export const chatId = writable('');
export const chatTitle = writable('');

export const chats = writable<Chat[]>([]);
export const pinnedChats = writable<Chat[]>([]);
export const tags = writable([]);

export const models: Writable<Model[]> = writable([]);

export const prompts: Writable<null | Prompt[]> = writable(null);
export const knowledge: Writable<null | Document[]> = writable(null);
export const tools = writable(null);
export const functions = writable(null);

export const banners: Writable<Banner[]> = writable([]);

export const settings: Writable<Partial<Settings>> = writable({});

export const showSidebar = writable(false);
export const showSettings = writable(false);
export const showArchivedChats = writable(false);
export const showChangelog = writable(false);

export const showControls = writable(false);
export const showOverview = writable(false);
export const showArtifacts = writable(false);
export const showCallOverlay = writable(false);

export const temporaryChatEnabled = writable(false);
export const scrollPaginationEnabled = writable(false);
export const currentChatPage = writable(1);

export type Model = OpenAIModel | OllamaModel | PersonaModel;

type BaseModel = {
	id: string;
	name: string;
	privategpt_name?: string;
	info?: ModelConfig;
	owned_by: 'ollama' | 'openai' | 'arena' | 'nebul-litellm';
	type?: 'base_model' | 'persona';
	is_active?: boolean;
};

export interface PersonaModel extends BaseModel {
	type: 'persona';
	is_active: boolean;
	meta?: {
		description?: string;
		profile_image_url?: string;
		tags?: {name: string}[];
	};
	base_model_id?: string;
}

export interface OpenAIModel extends BaseModel {
	owned_by: 'openai';
	external: boolean;
	source?: string;
}

export interface OllamaModel extends BaseModel {
	owned_by: 'ollama';
	details: OllamaModelDetails;
	size: number;
	description: string;
	model: string;
	modified_at: string;
	digest: string;
	ollama?: {
		name?: string;
		model?: string;
		modified_at: string;
		size?: number;
		digest?: string;
		details?: {
			parent_model?: string;
			format?: string;
			family?: string;
			families?: string[];
			parameter_size?: string;
			quantization_level?: string;
		};
		urls?: number[];
	};
}

type OllamaModelDetails = {
	parent_model: string;
	format: string;
	family: string;
	families: string[] | null;
	parameter_size: string;
	quantization_level: string;
};

type Settings = {
	models?: string[];
	conversationMode?: boolean;
	speechAutoSend?: boolean;
	responseAutoPlayback?: boolean;
	audio?: AudioSettings;
	showUsername?: boolean;
	notificationEnabled?: boolean;
	title?: TitleSettings;
	splitLargeDeltas?: boolean;
	chatDirection: 'LTR' | 'RTL';
	location?: UserCoordinates;
	backgroundImageUrl?: string;
	system?: string;
	requestFormat?: string;
	keepAlive?: string;
	seed?: number;
	temperature?: string;
	repeat_penalty?: string;
	top_k?: string;
	top_p?: string;
	num_ctx?: string;
	num_batch?: string;
	num_keep?: string;
	options?: ModelOptions;
};

type ModelOptions = {
	stop?: boolean;
};

type AudioSettings = {
	STTEngine?: string;
	TTSEngine?: string;
	speaker?: string;
	model?: string;
	nonLocalVoices?: boolean;
};

type TitleSettings = {
	auto?: boolean;
	model?: string;
	modelExternal?: string;
	prompt?: string;
};

type Prompt = {
	command: string;
	user_id: string;
	title: string;
	content: string;
	timestamp: number;
};

type Document = {
	collection_name: string;
	filename: string;
	name: string;
	title: string;
};

type Config = {
	status: boolean;
	name: string;
	version: string;
	default_locale: string;
	maintenance_mode: boolean;
	maintenance_mode_type: 'page' | 'banner';
	maintenance_message: string;
	default_models: string;
	default_prompt_suggestions: PromptSuggestion[];
	features: {
		auth: boolean;
		auth_trusted_header: boolean;
		enable_api_key: boolean;
		enable_signup: boolean;
		enable_login_form: boolean;
		enable_web_search?: boolean;
		enable_image_generation: boolean;
		enable_admin_export: boolean;
		enable_admin_chat_access: boolean;
		enable_community_sharing: boolean;
		nebul_show_whats_new: boolean;
		nebul_show_socials: boolean;
		nebul_show_audio: boolean;
		nebul_show_microphone_input: boolean;
		nebul_show_chat_controls: boolean;
		nebul_show_check_for_updates: boolean;
		nebul_show_version_open_webui: boolean;
		nebul_show_version: boolean;
		nebul_show_artifacts: boolean;
		nebul_show_system_prompt: boolean;
		nebul_show_advanced_params: boolean;
		nebul_show_interface_voice_settings: boolean;
		nebul_show_personalization_settings: boolean;
		nebul_show_language_contribution: boolean;
		nebul_version_toast_popup: boolean;
		nebul_show_changelog_modal: boolean;
		nebul_password_in_admin_settings: boolean;
		nebul_knowledge_sync_folder: boolean;
		nebul_knowledge_add_text_content: boolean;
		nebul_show_call_button: boolean;
		nebul_show_notifications_in_settings: boolean;
		nebul_show_all_languages: boolean;
		nebul_show_all_colour_themes: boolean;
		nebul_show_interface_settings: boolean;
		nebul_show_chats_settings: boolean;
		nebul_show_update_password: boolean;
		nebul_show_api_keys_settings: boolean;
		nebul_show_add_model: boolean;
		nebul_chat_share: boolean;
		nebul_show_create_user: boolean;
		nebul_show_suggestions: boolean;
		nebul_show_share_prompts: boolean;
		nebul_enable_message_rating: boolean;
		nebul_show_chat_export_webhook_button: boolean;
		nebul_chat_export_webhook_endpoint: string;
	};
	// oauth: {
	// 	providers: {
	// 		[key: string]: string;
	// 	};
	// };
};

type PromptSuggestion = {
	content: string;
	title: [string, string];
};

export type SessionUser = {
	id: string;
	email: string;
	name: string;
	role: string;
	profile_image_url: string;
	permissions?: {
		workspace?: {
			models?: boolean;
			knowledge?: boolean;
			prompts?: boolean;
			tools?: boolean;
			users?: boolean;
		};
	};
};
